import { useState, useEffect, useCallback, useRef } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { ConfirmModal } from '@/components/ConfirmModal';

const _wg = (name: string) => (...args: any[]) => (window as any)[name](...args);
const scrollToLastMobileCard = _wg('scrollToLastMobileCard');

interface CaseReportVehiclesInvolvedProps {
    value: any[];
    isReadOnly?: boolean;
    canSaveNpsdData?: boolean;
    onChange?: (vehicles: any[]) => void;
    onAddExisting?: (type: string) => void;
    onAddNew?: (type: string) => void;
    onAddUnknown?: (type: string) => void;
    onViewItem?: (type: string, id: any) => void;
    onRemoveItem?: (type: string, item: any) => void;
}

export function CaseReportVehiclesInvolved({
    value: vehicles,
    isReadOnly = false,
    canSaveNpsdData = false,
    onChange,
    onAddExisting,
    onAddNew,
    onAddUnknown,
    onViewItem,
    onRemoveItem,
}: CaseReportVehiclesInvolvedProps) {
    const { theme } = useTheme();
    const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; item: any }>({ open: false, item: null });
    const prevLengthRef = useRef(vehicles?.length || 0);
    const rootRef = useRef<HTMLDivElement>(null);

    // Watch vehicles length for scroll-to-last
    useEffect(() => {
        const newLength = vehicles?.length || 0;
        if (newLength > prevLengthRef.current) {
            setTimeout(() => {
                if (rootRef.current) {
                    scrollToLastMobileCard(rootRef.current);
                }
            }, 0);
        }
        prevLengthRef.current = newLength;
    }, [vehicles?.length]);

    const confirmDelete = useCallback((vehicle: any) => {
        setDeleteConfirm({ open: true, item: vehicle });
    }, []);

    const doDelete = useCallback(() => {
        const item = deleteConfirm.item;
        if (!item) {
            setDeleteConfirm({ open: false, item: null });
            return;
        }
        onRemoveItem?.('Vehicle', item);
        setDeleteConfirm({ open: false, item: null });
    }, [deleteConfirm.item, onRemoveItem]);

    const cancelDelete = useCallback(() => {
        setDeleteConfirm({ open: false, item: null });
    }, []);

    const formatVehicleDesc = useCallback((v: any) => {
        const parts = [v.year, v.make, v.model].filter(Boolean);
        return parts.length > 0 ? parts.join(' ') : 'Unknown Vehicle';
    }, []);

    const formatPlate = useCallback((v: any) => {
        const parts = [v.plateState, v.plateNumber].filter(Boolean);
        return parts.length > 0 ? parts.join(' ') : '';
    }, []);

    return (
        <div className={'case-report-involved-grid' + (theme ? ' ' + theme : '')} ref={rootRef}>
            <div className="header">
                <h1>Vehicle(s) <span className="hidden-sm hidden-xs">Involved</span></h1>
                <div className="header-buttons">
                    {canSaveNpsdData && !isReadOnly && (
                        <button
                            type="button"
                            className="btn-add btn-secondary hidden-sm hidden-xs"
                            onClick={() => onAddUnknown?.('Vehicle')}
                        >
                            <i className="fas fa-plus"></i> UNKNOWN
                        </button>
                    )}
                    {canSaveNpsdData && !isReadOnly && (
                        <button
                            type="button"
                            className="btn-add btn-secondary"
                            onClick={() => onAddNew?.('Vehicle')}
                        >
                            <i className="fas fa-plus"></i> NEW
                        </button>
                    )}
                    {!isReadOnly && (
                        <button
                            type="button"
                            className="btn-add"
                            onClick={() => onAddExisting?.('Vehicle')}
                        >
                            <i className="fas fa-plus"></i> EXISTING
                        </button>
                    )}
                </div>
            </div>

            <div className="table-container">
                <table>
                    <thead>
                        <tr>
                            <th style={{ width: '25%' }}>Vehicle</th>
                            <th style={{ width: '12%' }}>Color</th>
                            <th style={{ width: '15%' }}>Plate</th>
                            <th style={{ width: '12%' }}>Make</th>
                            <th style={{ width: '12%' }}>Model</th>
                            <th style={{ width: '8%' }}>Year</th>
                            {!isReadOnly && <th style={{ width: '8%' }}>Actions</th>}
                        </tr>
                    </thead>
                    {vehicles.length === 0 ? (
                        <tbody>
                            <tr>
                                <td colSpan={isReadOnly ? 6 : 7} className="empty-cell">
                                    No vehicles involved
                                </td>
                            </tr>
                        </tbody>
                    ) : (
                        <tbody>
                            {vehicles.map((vehicle: any) => (
                                <tr key={vehicle.id}>
                                    <td>
                                        <a href="#" className="item-link" onClick={(e) => { e.preventDefault(); onViewItem?.('Vehicle', vehicle.id); }}>
                                            {formatVehicleDesc(vehicle)}
                                        </a>
                                    </td>
                                    <td>{vehicle.color || ''}</td>
                                    <td>{formatPlate(vehicle)}</td>
                                    <td>{vehicle.make || ''}</td>
                                    <td>{vehicle.model || ''}</td>
                                    <td>{vehicle.year || ''}</td>
                                    {!isReadOnly && (
                                        <td>
                                            <div className="actions">
                                                <button className="btn-delete" onClick={() => confirmDelete(vehicle)} title="Remove vehicle">
                                                    Remove
                                                </button>
                                            </div>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    )}
                </table>
            </div>

            <div className="mobile-cards">
                {vehicles.length === 0 ? (
                    <div className="empty-state">
                        <p>No vehicles involved</p>
                    </div>
                ) : (
                    vehicles.map((vehicle: any) => (
                        <div key={'mobile-' + vehicle.id} className="card">
                            <div className="card-header">
                                <div className="card-header-content">
                                    <a href="#" className="card-item-name" onClick={(e) => { e.preventDefault(); onViewItem?.('Vehicle', vehicle.id); }}>
                                        {formatVehicleDesc(vehicle)}
                                    </a>
                                </div>
                                {!isReadOnly && <button className="btn-delete" onClick={() => confirmDelete(vehicle)}>Remove</button>}
                            </div>

                            <div className="card-grid">
                                <div className="card-field">
                                    <div className="card-field-label">Color</div>
                                    <div className="card-field-value">{vehicle.color || '\u2014'}</div>
                                </div>
                                <div className="card-field">
                                    <div className="card-field-label">Plate</div>
                                    <div className="card-field-value">{formatPlate(vehicle) || '\u2014'}</div>
                                </div>
                                <div className="card-field">
                                    <div className="card-field-label">Make</div>
                                    <div className="card-field-value">{vehicle.make || '\u2014'}</div>
                                </div>
                                <div className="card-field">
                                    <div className="card-field-label">Model</div>
                                    <div className="card-field-value">{vehicle.model || '\u2014'}</div>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>

            <ConfirmModal
                open={deleteConfirm.open}
                title="Remove Vehicle?"
                messageHtml="Are you sure you want to remove this vehicle from the report?"
                confirmText="Remove"
                cancelText="Cancel"
                confirmClass="danger"
                onConfirm={doDelete}
                onCancel={cancelDelete}
            />
        </div>
    );
}

(window as any).CaseReportVehiclesInvolvedReact = CaseReportVehiclesInvolved;
